/**
 * System routes configuration for handle extraction.
 *
 * NOTE: These values are intentionally hardcoded here to keep this package
 * dependency-free — importing the Routes constants would pull in
 * @buildwithflux/models and break Cloudflare Workers (which don't have
 * process.env). The values match the Routes constants in
 * @buildwithflux/url's routes.ts - if you update routes.ts, update these
 * values too.
 */

/**
 * Routes that match EXACTLY (no sub-paths).
 * These are terminal routes that don't have children.
 */
const EXACT_SYSTEM_ROUTES = new Set([
    "/sitemap",
    "/search",
    "/copilot",
    "/login",
    "/signup",
    "/logout",
    "/reset-password",
    "/verify-email",
    "/signin",
    "/signout",
    "/redirect",
    "/pricing",
    "/buildwithflux",
    "/forgot-password",
    "/storybook",
    "/version.json",
    "/favicon.ico",
    "/robots.txt",
    "/manifest.json",
    "/service-worker.js",
]);

/**
 * Routes that match as prefixes (trailing / included).
 * These match both the exact path (e.g., "/blog") and sub-paths (e.g., "/blog/post").
 */
const PREFIX_SYSTEM_ROUTES = [
    "/p/",
    "/blog/",
    "/projects/",
    "/organization/",
    "/account/",
    "/auth/",
    "/api/",
    "/static/",
    "/fonts/",
    "/_test_/",
    "/internal/",
];

/**
 * Checks if a pathname is a system route (not a user/handle route)
 *
 * @param pathname The URL pathname to check
 * @returns true if this is a system route, false if it could be a handle route
 */
export function isSystemRoute(pathname: string): boolean {
    const normalizedPath = pathname.toLowerCase();

    // Check exact matches (O(1) lookup)
    if (EXACT_SYSTEM_ROUTES.has(normalizedPath)) {
        return true;
    }

    // Check prefix matches
    // Each prefix has trailing /, so we match:
    // - startsWith(prefix) for sub-paths (e.g., "/blog/" matches "/blog/post")
    // - exact match without trailing / (e.g., "/blog/" also matches "/blog")
    for (const prefix of PREFIX_SYSTEM_ROUTES) {
        if (normalizedPath.startsWith(prefix) || normalizedPath === prefix.slice(0, -1)) {
            return true;
        }
    }

    return false;
}

/**
 * Strips the .md extension from a path segment if present
 * This allows handle extraction from markdown URLs like /username.md
 *
 * @param segment The path segment to process
 * @returns The segment without .md extension (if present)
 */
function stripMdExtension(segment: string): string {
    if (segment.toLowerCase().endsWith(".md")) {
        return segment.slice(0, -3);
    }
    return segment;
}

/**
 * Extracts the handle from a URL pathname
 *
 * Supported patterns:
 * - /{handle} - User/org profile
 * - /{handle}.md - User/org profile markdown export
 * - /{handle}/settings - Profile sub-routes
 * - /{handle}/starred - Profile sub-routes
 * - /{handle}/{slug} - Document page
 * - /{handle}/{slug}.md - Document page markdown export
 * - /{handle}/{slug}/{component} - Local component page
 * - /enterprise/{handle} - Enterprise profile
 * - /enterprise/{handle}/... - Enterprise sub-routes
 *
 * Returns undefined for system routes (marketing, blog, static assets, auth, etc.)
 *
 * @param pathname The URL pathname (e.g., "/natarius/my-project" or "/natarius.md")
 * @returns The extracted handle (lowercase) or undefined if not a handle route
 */
export function extractHandleFromPath(pathname: string): string | undefined {
    return extractHandleAndSlugFromPath(pathname)?.handle;
}

/**
 * Result of extracting handle and slug from a URL pathname
 */
export interface HandleAndSlug {
    /** The extracted handle (lowercase) */
    handle: string;
    /** The project slug (second path segment), if present */
    slug: string | undefined;
}

/**
 * Extracts the handle and optional project slug from a URL pathname
 *
 * Supported patterns:
 * - /{handle} - User/org profile
 * - /{handle}.md - User/org profile markdown export
 * - /{handle}/settings - Profile sub-routes (slug = "settings")
 * - /{handle}/starred - Profile sub-routes (slug = "starred")
 * - /{handle}/{slug} - Document page
 * - /{handle}/{slug}.md - Document page markdown export
 * - /{handle}/{slug}/{component} - Local component page (slug = second segment)
 * - /enterprise/{handle} - Enterprise profile
 * - /enterprise/{handle}/... - Enterprise sub-routes
 *
 * Returns undefined for system routes (marketing, blog, static assets, auth, etc.)
 *
 * @param pathname The URL pathname (e.g., "/natarius/my-project" or "/natarius.md")
 * @returns The extracted handle (lowercase) and optional slug, or undefined if not a handle route
 */
export function extractHandleAndSlugFromPath(pathname: string): HandleAndSlug | undefined {
    // Check if this is a system route
    if (isSystemRoute(pathname)) {
        return undefined;
    }

    // Split path into segments, filtering out empty strings
    const segments = pathname.split("/").filter(Boolean);

    if (segments.length === 0) {
        return undefined;
    }

    // Handle enterprise routes: /enterprise/{handle}/...
    if (segments[0]?.toLowerCase() === "enterprise") {
        if (segments.length < 2) {
            return undefined;
        }
        // Strip .md extension if present (e.g., /enterprise/acme.md)
        const enterpriseHandle = stripMdExtension(segments[1] ?? "");
        if (enterpriseHandle && /^[a-zA-Z0-9][\w-]*$/.test(enterpriseHandle)) {
            const slug = segments.length >= 3 ? stripMdExtension(segments[2] ?? "") : undefined;
            return {handle: enterpriseHandle.toLowerCase(), slug: slug || undefined};
        }
        return undefined;
    }

    // Standard routes: /{handle}/...
    // The first segment is the handle - strip .md extension if present
    const handle = stripMdExtension(segments[0] ?? "");

    // Validate handle format (basic check - handles should be alphanumeric with hyphens/underscores)
    // This helps filter out paths that look like handles but aren't
    if (handle && /^[a-zA-Z0-9][\w-]*$/.test(handle)) {
        const slug = segments.length >= 2 ? stripMdExtension(segments[1] ?? "") : undefined;
        return {handle: handle.toLowerCase(), slug: slug || undefined};
    }

    return undefined;
}
